/* Chip — matches Figma Chip / Badge (node 1110:8302).
 *
 * Layout: pill-shaped inline-flex row with a 4px gap between slots
 * (dot → icon → label → dismiss). Horizontal padding is 8px on each
 * side, vertical 2px — this matches Figma's `pl-8 pr-8 py-2` pattern
 * and the `--radius-round` (200px = pill) shape.
 *
 * Typography: --text-micro-medium (12px / 20px line-height / 500 weight) with
 * --text-xs-tracking letter-spacing — same as Button xs, Link xs, and
 * Notification appName/timestamp. */
.chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: var(--spacing-2) var(--spacing-8);
  border-radius: var(--radius-round);
  font: var(--text-micro-medium);
  letter-spacing: var(--text-xs-tracking);
  box-sizing: border-box;
  /* Inline-flex defaults shrink to content; keeping vertical-align
   * stable prevents baseline drift when a chip sits inline with text. */
  vertical-align: middle;
}

.label {
  /* Label sits in its own span so the outer flex handles gap and so
   * truncation (if a consumer ever needs it) can be scoped here without
   * affecting the dot / icon / dismiss slots. */
  white-space: nowrap;
}

/* ─── Tones ─────────────────────────────────────────────────────── */

/* Label/dot/icon color is applied via `color` on the root so that
 * `<Icon tone="inherit">` inside the chip picks it up. Figma Green
 * maps to --text-success (#059669 light / #6ee7b7 dark); Figma Red
 * uses our --text-negative semantic. --text-negative resolves to
 * #f55951 in light (Figma's soft red, not the deeper red-600 of
 * #dc2626 shown in the design). The shift is accepted because
 * (a) there is no --chip-negative-text token in tokens-figma.css and
 * (b) --text-negative is the project's canonical "negative state"
 * color everywhere else. Flagged in design-asks for a follow-up
 * dedicated token if design wants the deeper red. */
.tone-success {
  color: var(--text-success);
}

.tone-negative {
  color: var(--text-negative);
}

/* ─── Fill ──────────────────────────────────────────────────────── */

/* Filled background tint. tokens-figma.css has no green-50 / red-50
 * surface tokens — these are raw Tailwind-style tint literals from
 * the Figma spec. The dark-mode values are inferred from the
 * design's dark preview (screenshot in fileKey QkVMIm6TUdblppfqYlUtai
 * node 1110:8302): a deeply saturated but low-luminance tint that
 * keeps --text-success / --text-negative readable.
 *
 * Flagged in design-asks as `--chip-success-bg` / `--chip-negative-bg`
 * — once those tokens land, replace the literals here in one pass. */
.filled.tone-success {
  background: #ecfdf5;
}

.filled.tone-negative {
  background: #fef2f2;
}

[data-theme="dark"] .filled.tone-success {
  background: #052e16;
}

[data-theme="dark"] .filled.tone-negative {
  background: #450a0a;
}

/* Unfilled chips are fully transparent — no background, no border.
 * The class exists so consumers can override via composition if
 * needed, and so CSS Modules exports it for the className join. */
.unfilled {
  background: transparent;
}

/* ─── Dot ───────────────────────────────────────────────────────── */

/* 5×5 colored dot matching the chip's text color. `currentColor`
 * picks up the tone applied to the root, so no per-tone rule needed. */
.dot {
  width: 5px;
  height: 5px;
  border-radius: var(--radius-circle);
  background: currentColor;
  flex-shrink: 0;
}

/* ─── Dismiss ───────────────────────────────────────────────────── */

/* The dismiss X is a nested <button>. Defaults: no padding, no
 * border, transparent background. Color inherits the chip's tone so
 * the X matches the label. Small hover tint darkens the X slightly
 * without adding a full button surface — the chip is compact enough
 * that a separate hover box would dominate the visual. */
.dismiss {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  margin: 0;
  border: none;
  background: transparent;
  color: inherit;
  cursor: pointer;
  border-radius: 2px;
  transition: opacity var(--motion-duration-fast);
  /* The X is full-opacity by default; a slight dim on idle keeps
   * the label dominant. Hover pops it back to full opacity. */
  opacity: 0.7;
}

.dismiss:hover {
  opacity: 1;
}

.dismiss:focus-visible {
  outline: none;
  /* Match Button / IconButton focus ring — keeps sibling interactive
   * primitives consistent under keyboard nav. */
  box-shadow: 0 0 0 1px var(--text-primary);
  opacity: 1;
}
